"use client";

import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, Zap, Gem } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const principles = [{
  title: "Legal Clarity",
  description: "Every trademark in our portfolio undergoes rigorous legal vetting. We ensure clean titles, proper classifications, and seamless transfer processes under the Trade Marks Act, 1999, eliminating compliance risks.",
  icon: ShieldCheck,
  containerClass: "border-chart-3/20 bg-chart-3/5 hover:bg-chart-3/10 hover:border-chart-3/40",
  iconClass: "text-chart-3 bg-chart-3/10"
}, {
  title: "Fast Execution",
  description: "Skip the typical 6–12 month waiting period for trademark registration. Our curated assets are ready for immediate acquisition, allowing you to launch your brand and enter the market in days, not months.",
  icon: Zap,
  containerClass: "border-primary/20 bg-primary/5 hover:bg-primary/10 hover:border-primary/40",
  iconClass: "text-primary bg-primary/10"
}, {
  title: "Premium Curation",
  description: "We don't just list names; we curate high-potential brand assets across key industries like FMCG, wellness, and D2C. Each trademark is selected for its strong positioning and multi-class expansion potential.",
  icon: Gem,
  containerClass: "border-chart-2/20 bg-chart-2/5 hover:bg-chart-2/10 hover:border-chart-2/40",
  iconClass: "text-chart-2 bg-chart-2/10"
}];
export default function OurGuidingPrinciples() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="our-guiding-principles" className="dark bg-background text-foreground py-24 px-4 md:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6 font-serif">
            The Golden Standard of Brand Assets
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Our approach is built on three core pillars designed to provide entrepreneurs and investors with secure, rapid, and high-value market entry.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-1000 delay-200 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {principles.map((principle, i) => <div data-index={i} key={i} className="h-full group perspective-1000">
              <Card className={`h-full flex flex-col border backdrop-blur-sm transition-all duration-500 ease-out group-hover:-translate-y-3 group-hover:rotate-1 group-hover:shadow-2xl ${principle.containerClass}`}>
                <CardHeader className="pb-4">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-500 group-hover:scale-110 ${principle.iconClass}`}>
                    <principle.icon className="w-8 h-8" strokeWidth={1.5} />
                  </div>
                  <CardTitle className="text-2xl font-serif tracking-tight">
                    {principle.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <CardDescription className="text-base text-muted-foreground/90 leading-relaxed font-sans">
                    {principle.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}